import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Users } from "lucide-react";
import TeamSelector from "./TeamSelector";
import { TEAM_NAMES, TEAM_COLORS } from "../../../core/constants";
import "./Lobby.css";
import { GameController } from "../../controller/GameController";
import { GameState } from "../../../core/protocol/types";

interface LobbyProps {
  gameState: GameState;
  gameController: GameController | null;
}

const Lobby: React.FC<LobbyProps> = ({ gameState, gameController }) => {
  const { peerId } = useParams<{ peerId: string }>();
  const [gameEndType, setGameEndType] = useState<"frag" | "time">("frag");
  const [gameEndValue, setGameEndValue] = useState<number>(10);
  const [copyMessage, setCopyMessage] = useState<string>("");

  // Initialize game options from gameState when available
  useEffect(() => {
    if (gameState.params) {
      const currentEndType = gameState.params.gameEndType;
      if (currentEndType === "frag" || currentEndType === "time") {
        setGameEndType(currentEndType);
      } else {
        setGameEndType("frag");
      }
      if (gameState.params.gameEndValue && gameState.params.gameEndValue > 0) {
        setGameEndValue(gameState.params.gameEndValue);
      }
    }
  }, [gameState.params]);

  const players = Array.from(gameState.players.values());
  const localPlayer = gameState.localConnectionId
    ? gameState.connections.get(gameState.localConnectionId)
    : undefined;
  const currentTeam = localPlayer?.team ?? 0;
  const localNumPlayer = localPlayer?.numPlayer;
  
  const isHost = gameState.localConnectionId === gameState.hostConnectionId;
  const canEditOptions = isHost && gameState.status === "waiting";

  const handleGameEndTypeChange = (newType: "frag" | "time") => {
    if (!canEditOptions || !gameController) return;
    setGameEndType(newType);
    updateGameOptions({ gameEndType: newType });
  };

  const handleGameEndValueChange = (newValue: number) => {
    if (!canEditOptions || !gameController) return;
    setGameEndValue(newValue);
    updateGameOptions({ gameEndValue: newValue });
  };

  const updateGameOptions = (options: { gameEndType?: "frag" | "time"; gameEndValue?: number }) => {
    if (!gameController || !canEditOptions) return;
    gameController.updateGameOptions(options);
  };

  const handleStartGame = (e?: React.MouseEvent) => {
    if (e) {
        e.stopPropagation();
        e.preventDefault();
    }
    if (gameController && isHost && gameState.status === "waiting") {
      gameController.requestPauseToggle();
    }
  };

  const hostId = useMemo(() => {
    if (peerId) {
      return peerId;
    }
    if (gameState.localConnectionId) {
      return gameState.localConnectionId;
    }
    if (typeof gameController?.getPeerId === "function") {
      const controllerPeerId = gameController.getPeerId();
      if (controllerPeerId) {
        return controllerPeerId;
      }
    }
    return "";
  }, [peerId, gameState.localConnectionId, gameController]);

  const joinUrl =
    hostId && typeof window !== "undefined"
      ? `${window.location.origin}/multi-player/join/${encodeURIComponent(hostId)}`
      : "";

  const handleCopyLink = () => {
    if (!joinUrl) {
      setCopyMessage("Host ID unavailable");
      setTimeout(() => setCopyMessage(""), 3000);
      return;
    }
    navigator.clipboard
      .writeText(joinUrl)
      .then(() => {
        setCopyMessage("Join link copied to clipboard!");
        setTimeout(() => setCopyMessage(""), 3000);
      })
      .catch((err) => {
        console.error("Failed to copy URL:", err);
        setCopyMessage("Failed to copy link");
        setTimeout(() => setCopyMessage(""), 3000);
      });
  };

  const handleShareWhatsApp = () => {
    if (!joinUrl) {
      setCopyMessage("Host ID unavailable");
      setTimeout(() => setCopyMessage(""), 3000);
      return;
    }
    const message = encodeURIComponent(
      `Join my Quadra multiplayer game! ${joinUrl}`
    );
    const whatsappUrl = `https://wa.me/?text=${message}`;
    window.open(whatsappUrl, "_blank");
  };

  // Debug: Log team changes
  useEffect(() => {
    if (localPlayer) {
      console.log(
        `[Lobby] Local player team: ${localPlayer.team}, currentTeam: ${currentTeam}, connectionId: ${gameState.localConnectionId}, hostConnectionId: ${gameState.hostConnectionId}, isHost: ${isHost}`
      );
    }
  }, [localPlayer, currentTeam, gameState.localConnectionId, gameState.hostConnectionId, isHost]);

  return (
    <div className="lobby-wrapper">
      <div className="lobby-content">
        <div className="lobby-header">
          <h1 className="lobby-title">
            {isHost ? "Host Game" : "Waiting for Host"}
          </h1>
          <div className="lobby-player-count">
            <Users size={20} />
            <span>{players.length} {players.length === 1 ? "Player" : "Players"}</span>
          </div>
        </div>

        {/* Game Options Section */}
        <div className="lobby-section">
          <h2 className="lobby-section-title">Game Options</h2>
          <div className="game-options">
            <div className="game-option-group">
              <label className="game-option-label">Game End Condition</label>
              <div className="game-option-radio-group">
                <label
                  className={`game-option-radio ${
                    gameEndType === "frag" ? "active" : ""
                  } ${!canEditOptions ? "disabled" : ""}`}
                >
                  <input
                    type="radio"
                    name="gameEndType"
                    value="frag"
                    checked={gameEndType === "frag"}
                    onChange={() => handleGameEndTypeChange("frag")}
                    disabled={!canEditOptions}
                  />
                  <span>Frag Limit</span>
                </label>
                <label
                  className={`game-option-radio ${
                    gameEndType === "time" ? "active" : ""
                  } ${!canEditOptions ? "disabled" : ""}`}
                >
                  <input
                    type="radio"
                    name="gameEndType"
                    value="time"
                    checked={gameEndType === "time"}
                    onChange={() => handleGameEndTypeChange("time")}
                    disabled={!canEditOptions}
                  />
                  <span>Time Limit</span>
                </label>
              </div>
              {!canEditOptions && (
                <p className="game-option-hint">
                  Only the host can change game options
                </p>
              )}
            </div>

            <div className="game-option-group">
              <label className="game-option-label">
                {gameEndType === "frag" ? "Frag Limit" : "Time Limit (minutes)"}
              </label>
              <input
                type="number"
                min="1"
                max={gameEndType === "frag" ? "9999" : "999"}
                value={gameEndValue}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value > 0) {
                    handleGameEndValueChange(value);
                  }
                }}
                disabled={!canEditOptions}
                className="game-option-input"
              />
            </div>
          </div>
        </div>

        {/* Team Selection Section */}
        <div className="lobby-section">
          <h2 className="lobby-section-title">Select Team</h2>
          <TeamSelector
            currentTeam={currentTeam}
            players={players}
            onTeamChange={(newTeam) => {
              if (gameController) {
                gameController.changeTeam(newTeam);
              }
            }}
            isServer={isHost}
            gameStatus={gameState.status}
          />
        </div>

        {/* Players List */}
        <div className="lobby-section">
          <h2 className="lobby-section-title">Players</h2>
          <div className="players-list">
            {players.length === 0 ? (
              <p className="no-players">No players connected</p>
            ) : (
              players.map((player) => {
                const teamIndex = player.team ?? 0;
                const teamName = TEAM_NAMES[teamIndex] || "Unknown";
                const teamColor = TEAM_COLORS[teamIndex] || "#FFFFFF";
                // Use numPlayer for consistency with game logic (matches original design)
                const isLocal = localNumPlayer !== undefined && player.numPlayer === localNumPlayer;

                return (
                  <div
                    key={player.connectionId}
                    className={`player-item ${isLocal ? "local-player" : ""}`}
                  >
                    <div
                      className="player-team-indicator"
                      style={{ backgroundColor: teamColor }}
                    />
                    <div className="player-info">
                      <div className="player-name">
                        {player.name}
                        {isLocal && <span className="player-you"> (You)</span>}
                        {player.connectionId === gameState.hostConnectionId && (
                          <span className="player-host"> (Host)</span>
                        )}
                      </div>
                      <div className="player-team" style={{ color: teamColor }}>
                        Team {teamName}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Host Controls */}
        {isHost && (
          <div className="lobby-section">
            <div className="host-controls">
              <button
                className="start-game-button"
                onClick={handleStartGame}
                disabled={gameState.status !== "waiting"}
              >
                ▶ Start Game
              </button>
              <div className="host-actions">
                <div className="invite-section">
                  <label className="invite-label">Invite friends:</label>
                  <div className="share-buttons-group">
                    <button
                      className="share-whatsapp-button"
                        onClick={handleShareWhatsApp}
                        disabled={!joinUrl}
                    >
                      💬 WhatsApp
                    </button>
                    <button
                      className="copy-link-button"
                        onClick={handleCopyLink}
                        disabled={!joinUrl}
                    >
                      📋 Copy Link
                    </button>
                  </div>
                </div>
                {copyMessage && (
                  <div className="copy-message">{copyMessage}</div>
                )}
                {!joinUrl && (
                  <p className="game-option-hint">
                    Waiting for host ID… share buttons will enable once ready.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Lobby;
